import { money, planDisplayName } from './formatters';
import { formatDayMonth, formatDayMonthYear, formatDayMonthYearTime, formatMonthYear } from '../../../utils/dateFormat';
import type {
  CustomerInvoice,
  CustomerRow,
  CustomerStage,
  InvoiceInsightKind,
  InvoiceThreadInsight,
  PaymentStatus,
  SubscriptionDetailPhase,
  SubscriptionPhase,
} from '../../../api/customers';

/**
 * Words and colours for the Customer status tab and the customer detail page.
 * One place, so a badge on the list and the same badge on the page never drift.
 */

export type BadgeVariant = 'success' | 'warning' | 'error' | 'default' | 'info';

export const STAGE_BADGE: Record<CustomerStage, { variant: BadgeVariant; label: string }> = {
  active: { variant: 'success', label: 'Active' },
  onboarding: { variant: 'warning', label: 'Onboarding' },
  former: { variant: 'default', label: 'Former' },
  unknown: { variant: 'default', label: 'Unknown' },
};

export const BLOCKED_LABEL: Record<string, string> = {
  odoo_link: 'Not linked to Odoo',
  subscription: 'No live subscription',
  invoice: 'Not invoiced',
  send: 'Invoice not sent',
  payment: 'Awaiting payment',
};

export const PAYMENT_BADGE: Record<PaymentStatus, { variant: BadgeVariant; label: string }> = {
  paid: { variant: 'success', label: 'Paid' },
  outstanding: { variant: 'warning', label: 'Outstanding' },
  overdue: { variant: 'error', label: 'Overdue' },
  not_invoiced: { variant: 'default', label: 'Not invoiced' },
  unknown: { variant: 'default', label: 'Unknown' },
};

/**
 * A contract that has not started is not a live one, and a contract that has
 * ended is not either. Saying which it is costs one word and is the difference
 * between a renewal that is due and one that has already lapsed.
 */
export const PHASE_LABEL: Record<SubscriptionDetailPhase, string> = {
  live: 'Live',
  scheduled: 'Starts later',
  ended: 'Ended',
  quotation: 'Quotation',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

export const PHASE_VARIANT: Record<SubscriptionDetailPhase, BadgeVariant> = {
  live: 'success',
  scheduled: 'warning',
  ended: 'error',
  quotation: 'default',
  cancelled: 'default',
  unknown: 'default',
};

/**
 * An ended order that a renewal carries on is not churn: a neutral "Renewed",
 * with red kept for an order that ended with nothing after it.
 */
export function subscriptionPhaseBadge(
  phase: SubscriptionDetailPhase,
  renewedBy: string | null | undefined,
): { variant: BadgeVariant; label: string; title?: string } {
  if (phase === 'ended' && renewedBy) return { variant: 'default', label: 'Renewed', title: `Renewed by ${renewedBy}` };
  return { variant: PHASE_VARIANT[phase], label: PHASE_LABEL[phase] };
}

/** "12 Mar 2026" — or a dash when there is no date. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return formatDayMonthYear(iso) || '—';
}

/** "21 Sep" — for a day that sits next to something already dated. */
export function dayMonth(iso: string): string {
  return formatDayMonth(iso);
}

/** "September 2026" — timeline month headings. */
export function monthHeading(iso: string): string {
  return formatMonthYear(iso);
}

/** "12 Sep 2026, 14:05" — for mails, where the hour tells two same-day mails apart. */
export function shortDateTime(iso: string): string {
  return formatDayMonthYearTime(iso);
}

/**
 * The reminder ladder's tier keys, as the payment-reminder automation writes
 * them, in words. The old keys (upcoming, due_today, …) still sit in state for
 * invoices reminded before the ladder was renamed, so both sets are read.
 */
const REMINDER_TIER_LABEL: Record<string, string> = {
  // Same wording as the reminder automation's own ladder.
  after_send: 'sent, checking in',
  before_due_week: 'due in a week',
  before_due_final: 'due tomorrow',
  upcoming: 'due in a week',
  due_today: 'due-date reminder',
  overdue_week: '1 week overdue',
  overdue_three_weeks: '3 weeks overdue',
  // Built from what the customer wrote, not from the due date.
  payment_claim_check: 'payment claimed, not received',
  promise_missed: 'promised date missed',
};

/** The tier in words, lower case so it reads inside a sentence ("Reminder: due in a week"). */
export function reminderTierLabel(tier: string): string {
  const known = REMINDER_TIER_LABEL[tier];
  if (known) return known;
  const weeks = /^overdue_(\d+)w$/.exec(tier);
  if (weeks) return `${weeks[1]} week${weeks[1] === '1' ? '' : 's'} overdue`;
  return tier.replace(/_/g, ' ');
}

/** "7d before due · 21 Sep", or a dash when the invoice was never reminded. */
export function reminderLabel(invoice: CustomerInvoice): string {
  if (!invoice.reminder) return '—';
  const words = reminderTierLabel(invoice.reminder.tier);
  const tier = words.charAt(0).toUpperCase() + words.slice(1);
  return invoice.reminder.at ? `${tier} · ${dayMonth(invoice.reminder.at)}` : tier;
}

/**
 * What the customer's latest mail on an invoice thread says, as a badge. Null
 * for `none`, so a thread with nothing actionable shows nothing at all.
 */
const INSIGHT_BADGE: Record<Exclude<InvoiceInsightKind, 'none'>, { variant: BadgeVariant; label: string }> = {
  payment_sent: { variant: 'success', label: 'Says paid' },
  will_pay_on: { variant: 'info', label: 'Promised' },
  redirect_recipient: { variant: 'warning', label: 'Send to new contact' },
  dispute: { variant: 'error', label: 'Dispute' },
  question: { variant: 'warning', label: 'Question' },
};

export function insightBadge(kind: InvoiceInsightKind): { variant: BadgeVariant; label: string } | null {
  return kind === 'none' ? null : INSIGHT_BADGE[kind];
}

/** An insight worth showing: present and not `none`. */
export function hasInsight(invoice: CustomerInvoice): invoice is CustomerInvoice & { insight: InvoiceThreadInsight } {
  return invoice.insight !== null && invoice.insight.kind !== 'none';
}

/**
 * A "send it to somebody else" we already acted on. Same rule as the API's
 * `redirectPending`: a mail to the new recipient on the thread (ISS UK: resent
 * to resolution.team@ on 18 Sep), or a reminder drafted after the request.
 */
export function redirectSettled(invoice: Pick<CustomerInvoice, 'insight' | 'reminder'>): boolean {
  const insight = invoice.insight;
  if (!insight || insight.kind !== 'redirect_recipient') return false;
  if (insight.recipientsMailedAt) return true;
  return Boolean(invoice.reminder?.at && invoice.reminder.at >= insight.mailAt.slice(0, 10));
}

/**
 * The pill an invoice's insight earns, or null: none for `none`, and none for a
 * redirect already settled, which has nothing left to do. Every surface (list,
 * detail table, mail thread) reads it here so they cannot disagree.
 */
export function invoiceInsightBadge(
  invoice: Pick<CustomerInvoice, 'insight' | 'reminder'>,
): { variant: BadgeVariant; label: string } | null {
  if (!invoice.insight || invoice.insight.kind === 'none') return null;
  if (redirectSettled(invoice)) return null;
  return insightBadge(invoice.insight.kind);
}

/** The most recently read actionable insight across a customer's open invoices, for the list row. */
export function latestInsight(customer: CustomerRow): InvoiceThreadInsight | null {
  let best: InvoiceThreadInsight | null = null;
  for (const invoice of customer.invoicing.invoices) {
    if (!isOpenInvoice(invoice) || !hasInsight(invoice)) continue;
    // A redirect we already acted on is settled: nothing left to flag on the row.
    if (redirectSettled(invoice)) continue;
    if (best === null || invoice.insight.mailAt > best.mailAt) best = invoice.insight;
  }
  return best;
}

const isFutureDay = (iso: string) => new Date(iso) > new Date();

/** True while the automation stands down on the customer's word (they said paid or named a date). */
export function remindersPausedByInsight(insight: InvoiceThreadInsight): boolean {
  if (/paused/i.test(insight.effect)) return true;
  return (
    (insight.kind === 'payment_sent' || insight.kind === 'will_pay_on') &&
    insight.followUpOn !== null &&
    isFutureDay(insight.followUpOn)
  );
}

/** The follow-up day when it is still ahead, so the card can show "Check on 8 Oct". */
export function pendingFollowUp(insight: InvoiceThreadInsight): string | null {
  return insight.followUpOn && isFutureDay(insight.followUpOn) ? insight.followUpOn : null;
}

/** "Customer said paid on 24 Sep; 14 days later, still not received" — the basis note for an insight-built draft. */
export function insightDraftNote(insight: InvoiceThreadInsight | null): string {
  if (!insight) return 'Built from what the customer wrote';
  const claimed = insight.claimedDate ?? insight.mailAt;
  if (insight.kind === 'will_pay_on') {
    return `Promised by ${dayMonth(claimed)}; not received`;
  }
  const days = Math.max(0, Math.round((Date.now() - new Date(claimed).getTime()) / 86_400_000));
  return `Customer said paid on ${dayMonth(claimed)}; ${days} days later, still not received`;
}

/** The chase digest's item kinds, in words. */
const CHASE_KIND_LABEL: Record<string, string> = {
  odoo_link: 'not linked to Odoo',
  subscription: 'no subscription in Odoo',
  invoice: 'no invoice created',
  send: 'invoice not sent',
  reply_needed: 'customer needs a reply',
};

export const chaseKindLabel = (kind: string) => CHASE_KIND_LABEL[kind] ?? kind.replace(/_/g, ' ');

/** Dated after today: booked in Odoo but not owed yet. The API decides, on the Brussels day. */
export const isFutureDated = (invoice: CustomerInvoice) => invoice.scheduled;

/** "36,400 USD" next to the EUR figure when the document was not issued in EUR. */
export function documentAmount(invoice: CustomerInvoice): string | null {
  if (invoice.documentCurrency === invoice.currency) return null;
  return money(invoice.documentTotal, invoice.documentCurrency);
}

/** Everything the API could not determine reads the same way, and never as €0. */
export const UNKNOWN = 'unknown';

/**
 * The customer's MRR as this month counts it — the Overview's figure, with the
 * MRR sheet's overrides and spread setup fees — falling back to the live Odoo
 * contracts only when the MRR engine could not be read.
 */
export function mrrLabel(customer: CustomerRow): string {
  const { mrr, liveCount, sourceMrr, sourceCurrency, reportedMrr } = customer.subscription;
  if (liveCount === 0) return '—';
  if (mrr === null) {
    // The order's own figure is still worth showing; it just isn't in EUR yet.
    return sourceMrr !== null && sourceCurrency
      ? `${money(sourceMrr, sourceCurrency)} · unconverted`
      : UNKNOWN;
  }
  return money(reportedMrr ?? mrr, 'EUR');
}

/** What a row adds to an MRR total: the reported figure, else the live contracts. */
export function mrrValue(customer: CustomerRow): number {
  const { reportedMrr, mrr, liveCount } = customer.subscription;
  if (reportedMrr !== null) return reportedMrr;
  return liveCount > 0 ? (mrr ?? 0) : 0;
}

/**
 * The Odoo contract amount in the order's own currency, with the EUR beside it
 * when they differ: "$7,044 (€6,300)" for ISS North America, "€1,875" for Swiss GRC.
 */
export function odooContractText(eur: number, source: number | null | undefined, currency: string | null | undefined): string {
  if (!currency || currency === 'EUR' || source === null || source === undefined) return money(eur, 'EUR');
  return `${money(source, currency)} (${money(eur, 'EUR')})`;
}

/** The Odoo amount the override replaces, in the order's currency when there is one live order. */
function overrideOdooText(customer: CustomerRow): string | null {
  const o = customer.subscription.mrrOverride;
  if (!o) return null;
  const sub = customer.subscription;
  return sub.liveCount === 1 ? odooContractText(o.contractMrr, sub.sourceMrr, sub.sourceCurrency) : money(o.contractMrr, 'EUR');
}

/**
 * The sheet's note as a short label: a currency note reads "invoiced in USD",
 * a short note stays as written, a long one becomes "MRR sheet override". The
 * full text belongs in a tooltip, once.
 */
export function overrideShortNote(note: string): string {
  const cur = /\b(USD|GBP|CHF|SEK|NOK|DKK|PLN)\b/.exec(note);
  if (cur && /invoic|bill/i.test(note)) return `invoiced in ${cur[1]}`;
  return note.length <= 30 ? note : 'MRR sheet override';
}

/** "Odoo €1,875 · override: Tier 2" — why the MRR is not the contract amount; the full note is the tooltip. */
export function mrrOverrideLabel(customer: CustomerRow): string | null {
  const o = customer.subscription.mrrOverride;
  return o ? `Odoo ${overrideOdooText(customer)} · override: ${overrideShortNote(o.note)}` : null;
}

/** The list's one-line version: the full note sits on the customer page and in the hover. */
export function mrrOverrideShort(customer: CustomerRow): string | null {
  return customer.subscription.mrrOverride ? `Odoo ${overrideOdooText(customer)} · MRR sheet override` : null;
}

/**
 * The Odoo legal names behind a customer when they differ from the Attio name
 * it is shown under ("ISS UK Ltd" under "ISS Facility Services UK"). Null when
 * Odoo calls it the same thing, or there is no Odoo partner.
 */
export function odooLegalName(customer: CustomerRow): string | null {
  const own = customer.companyName.trim().toLowerCase();
  const names = [...new Set(customer.odooPartners.map((p) => p.name))].filter((n) => n.trim().toLowerCase() !== own);
  return names.length ? names.join(' + ') : null;
}

export function outstandingLabel(customer: CustomerRow): string {
  if (customer.odooCompanyId === null) return UNKNOWN;
  const { outstanding, outstandingIncomplete } = customer.invoicing;
  if (outstanding <= 0 && !outstandingIncomplete) return '—';
  return `${money(outstanding, 'EUR')}${outstandingIncomplete ? '+' : ''}`;
}

/**
 * The plan as it is billed. Arendt's order says Monthly, but its 1 Dec 2025
 * invoice is 12 × €2,500 for the year, so "Monthly" alone made a customer with
 * no invoice since December look behind. A plan whose latest invoice covers a
 * year or more of its MRR says so.
 */
export function billingLabel(customer: CustomerRow): string | null {
  const sub = customer.subscription;
  if (!sub.planName) return null;
  const plan = planDisplayName(sub.planName);
  const monthly = sub.sourceMrr ?? sub.mrr;
  if (!/month/i.test(sub.planName) || !monthly || monthly <= 0) return plan;
  const latest = customer.invoicing.invoices
    .filter((i) => !i.isCreditNote && !i.scheduled && i.total > 0)
    .reduce<CustomerInvoice | null>((best, i) => (best === null || i.issueDate > best.issueDate ? i : best), null);
  if (!latest) return plan;
  const months = Math.round(latest.documentTotal / monthly);
  return months >= 12 && Math.abs(latest.documentTotal - months * monthly) <= monthly * 0.05
    ? `${plan}, invoiced yearly (${latest.invoiceNumber} = ${months} months)`
    : plan;
}

/**
 * "since 6 Aug 2026" is the deal; when the contract started months later (RCS
 * Lab: won 6 Aug, contract from 5 Oct, so it is new MRR in October), say both:
 * "won 6 Aug 2026 · from 5 Oct 2026". The list row and the detail header both
 * read it here.
 */
export function customerSinceLabel(customer: Pick<CustomerRow, 'closedWonAt' | 'subscription' | 'deals'>): string {
  const won = customer.closedWonAt;
  const starts = customer.subscription.startsOn;
  // Only a first contract: a renewal years later is not news on the row.
  const firstContract = customer.deals.length <= 1;
  if (firstContract && won && starts && starts.slice(0, 7) > won.slice(0, 7) && customer.subscription.phase !== 'ended') {
    return `won ${shortDate(won)} · from ${shortDate(starts)}`;
  }
  return `since ${shortDate(won)}`;
}

/** The contract badge text: "Starts 5 Oct 26" for a scheduled one, otherwise the phase. */
export function contractBadgeLabel(phase: SubscriptionPhase, startsOn: string | null): string {
  return phase === 'scheduled' && startsOn ? `Starts ${shortDate(startsOn)}` : PHASE_LABEL[phase];
}

/** Whether an invoice still has money on it (or is a draft that never went out). */
export function isOpenInvoice(invoice: CustomerInvoice): boolean {
  if (invoice.isCreditNote) return false;
  if (invoice.status === 'draft') return true;
  return invoice.residual !== null && invoice.residual > 0.005;
}

const isSettledDoc = (invoice: CustomerInvoice) =>
  invoice.residual !== null && Math.abs(invoice.residual) < 0.005 && invoice.status !== 'draft';

/**
 * What settled a document. The API says so from Odoo's payment list
 * (`settledBy`); builds before it existed fall back to Odoo's payment state,
 * where `reversed` is an invoice cleared by a credit note. Null = not settled,
 * or a settled credit note whose route the API did not say.
 */
export function settledByOf(invoice: CustomerInvoice): NonNullable<CustomerInvoice['settledBy']> | null {
  if (!isSettledDoc(invoice)) return null;
  if (invoice.settledBy) return invoice.settledBy;
  if (invoice.isCreditNote) return null;
  return invoice.paymentState === 'reversed' ? 'credit_note' : 'payment';
}

/**
 * The day a document was settled, as the page shows it. A credit note set
 * against an invoice cannot be settled before it was issued: older API builds
 * dated it with the reversed invoice's day (Swiss GRC RINV/2026/00004, issued
 * 22 Sep, "paid 28 Apr"), so the later of the two is used.
 */
export function settledDate(invoice: CustomerInvoice): string | null {
  const paid = invoice.paidDate;
  if (!paid || !isSettledDoc(invoice)) return null;
  if (invoice.isCreditNote && paid.slice(0, 10) < invoice.issueDate.slice(0, 10)) return invoice.issueDate;
  return paid;
}

/** A bank payment dated before the invoice (Eltel paid 17 Feb; INV/2026/00017 is dated 24 Feb). Odoo's own data. */
export function paidBeforeIssue(invoice: CustomerInvoice): boolean {
  const on = settledDate(invoice);
  return !invoice.isCreditNote && settledByOf(invoice) === 'payment' && !!on && on.slice(0, 10) < invoice.issueDate.slice(0, 10);
}

/**
 * Paid and credited real invoices. An invoice a credit note cleared was never
 * paid: Swiss GRC is "2 invoices · 1 credited", not "1 paid". Counted from the
 * invoices themselves so it holds against any API build.
 */
export function invoiceCounts(customer: CustomerRow): { paid: number; credited: number } {
  let paid = 0;
  let credited = 0;
  for (const i of customer.invoicing.invoices) {
    if (i.isCreditNote || i.scheduled) continue;
    const by = settledByOf(i);
    if (by === 'credit_note') credited += 1;
    else if (by !== null) paid += 1;
  }
  return { paid, credited };
}

/**
 * Credit notes the invoiced total does not net, by invoice number: the API's
 * rule (`creditedAgainstInvoices`), each nets one invoice of the same amount
 * dated on or before it. GELITA's RINV/2026/00002 (19 Mar) predates its only
 * invoice (20 Aug), so it reverses a document not listed here and is not
 * netted. Pure.
 */
export function unnettedCreditNotes(invoices: CustomerInvoice[]): Set<string> {
  const real = invoices
    .filter((d) => !d.isCreditNote && !d.scheduled && d.total > 0)
    .sort((a, b) => b.issueDate.localeCompare(a.issueDate));
  const used = new Set<number>();
  const out = new Set<string>();
  for (const note of invoices.filter((d) => d.isCreditNote && !d.scheduled)) {
    const amount = Math.abs(note.total);
    const day = note.issueDate.slice(0, 10);
    const match = real.findIndex(
      (inv, idx) => !used.has(idx) && inv.issueDate.slice(0, 10) <= day && Math.abs(inv.total - amount) < 0.5,
    );
    if (match < 0) out.add(note.invoiceNumber);
    else used.add(match);
  }
  return out;
}

/**
 * Credit notes paired with the invoices they cleared, both ways, by number.
 * The API names the pair (`settledAgainst`); older builds do not, so a credited
 * invoice is paired with a settled credit note of the same amount dated on or
 * after it (Swiss GRC: INV/2026/00025 ↔ RINV/2026/00004, both €30,000). Pure.
 */
export function creditPairs(invoices: CustomerInvoice[]): Map<string, string> {
  const pairs = new Map<string, string>();
  for (const i of invoices) if (i.settledAgainst) pairs.set(i.invoiceNumber, i.settledAgainst);
  const notes = invoices.filter((i) => i.isCreditNote && isSettledDoc(i) && !pairs.has(i.invoiceNumber));
  for (const inv of invoices) {
    if (inv.isCreditNote || pairs.has(inv.invoiceNumber) || settledByOf(inv) !== 'credit_note') continue;
    const note = notes.find(
      (n) =>
        !pairs.has(n.invoiceNumber) &&
        Math.abs(Math.abs(n.total) - inv.total) < 0.5 &&
        n.issueDate.slice(0, 10) >= inv.issueDate.slice(0, 10),
    );
    if (!note) continue;
    pairs.set(inv.invoiceNumber, note.invoiceNumber);
    pairs.set(note.invoiceNumber, inv.invoiceNumber);
  }
  return pairs;
}

/** "Paid" / "Credited" / "Overdue · 12d" / Odoo's own state — one badge for an invoice line. */
export function invoiceStateBadge(
  invoice: CustomerInvoice,
  /** The document it was paired with (`creditPairs`), for builds that do not name it. */
  pairedWith: string | null = null,
): { variant: BadgeVariant; label: string } {
  if (invoice.isCreditNote) {
    // A credit note is not paid: it is set against an invoice or paid back.
    const by = settledByOf(invoice);
    if (by === 'invoice') return { variant: 'default', label: 'Applied' };
    if (by === 'refund') return { variant: 'default', label: 'Refunded' };
    if (by === 'netted') return { variant: 'default', label: 'Netted in payment' };
    if (isSettledDoc(invoice) && pairedWith) return { variant: 'default', label: 'Applied' };
    if (isSettledDoc(invoice)) return { variant: 'default', label: 'Settled' };
    if (invoice.residual !== null && invoice.residual < -0.005) return { variant: 'default', label: 'Credit unused' };
  }
  if (isSettledDoc(invoice)) {
    return settledByOf(invoice) === 'credit_note'
      ? { variant: 'default', label: 'Credited' }
      : { variant: 'success', label: 'Paid' };
  }
  if (invoice.overdueDays) {
    return { variant: 'error', label: `Overdue · ${invoice.overdueDays}d` };
  }
  // Booked but dated after today: not open yet, nobody owes it.
  if (invoice.scheduled && !invoice.isCreditNote) {
    return { variant: 'default', label: 'Scheduled' };
  }
  return { variant: 'default', label: invoiceStateLabel(invoice.paymentState ?? invoice.status) };
}

/** Odoo's payment_state and move state values in plain words. */
const INVOICE_STATE_LABEL: Record<string, string> = {
  not_paid: 'Open',
  in_payment: 'In payment',
  paid: 'Paid',
  partial: 'Partially paid',
  reversed: 'Reversed',
  invoicing_legacy: 'Legacy',
  blocked: 'Blocked',
  draft: 'Draft',
  posted: 'Posted',
  cancel: 'Cancelled',
};

/** Never shows a raw snake_case Odoo value: unknown ones become "Some value". */
export function invoiceStateLabel(state: string | null | undefined): string {
  if (!state) return 'Unknown';
  const known = INVOICE_STATE_LABEL[state];
  if (known) return known;
  const words = state.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Invoiced net of credit notes. Arendt: €114,000 on four invoices less
 * RINV/2025/00003's €18,000 is €96,000. The API nets only credit notes that
 * reverse one of the customer's listed invoices. `totalCredited` is missing
 * from API builds before it existed, which reads as no credit notes.
 */
export function netInvoiced(invoicing: CustomerRow['invoicing']): number {
  return invoicing.totalInvoiced - (invoicing.totalCredited ?? 0);
}

/**
 * Paid net of credit notes: a credit note settles the invoice it reverses
 * without any money moving, so the settled total less the credits is what was
 * actually paid (Arendt €96,000, Eltel €24,000, Swiss GRC nothing yet).
 */
export function netPaid(invoicing: CustomerRow['invoicing']): number {
  return Math.max(0, invoicing.totalPaid - (invoicing.totalCredited ?? 0));
}

/**
 * Invoices one payment settled together, as a sentence, or null. Arendt's
 * INV/2025/00028, INV/2025/00048 and 202500030 (issued Jan–Jul 2025) were all
 * paid by one bank entry on 10 Feb 2026: real Odoo data, which reads as an
 * error unless the page says so. Grouped by the bank entry when the API names
 * it, else by the day. Pure.
 */
export function paidTogether(invoices: CustomerInvoice[]): string[] {
  const groups = new Map<string, { day: string; ref: string | null; numbers: string[] }>();
  for (const i of invoices) {
    if (i.isCreditNote || settledByOf(i) !== 'payment') continue;
    const day = settledDate(i)?.slice(0, 10);
    if (!day) continue;
    const key = i.settledRef ?? `day:${day}`;
    const g = groups.get(key) ?? { day, ref: i.settledRef ?? null, numbers: [] };
    g.numbers.push(i.invoiceNumber);
    groups.set(key, g);
  }
  return [...groups.values()]
    .filter((g) => g.numbers.length >= 2)
    .map((g) => {
      const list = `${g.numbers.slice(0, -1).join(', ')} and ${g.numbers[g.numbers.length - 1]}`;
      return g.ref
        ? `${list} were paid together by one bank payment on ${shortDate(g.day)} (${g.ref} in Odoo).`
        : `${list} were all settled the same day in Odoo, ${shortDate(g.day)}.`;
    });
}

/**
 * A contract that has ended but still counts in this month's MRR (nextbike by
 * TIER: ended 1 Oct, in October's MRR). The one lifecycle rule every surface
 * reads: "In MRR for Oct 2026 (contract ended 1 Oct 2026)". Null for a live
 * contract, and for an ended one that no longer counts.
 */
export function endedStillInMrr(customer: CustomerRow): { month: string; endedOn: string; text: string } | null {
  const sub = customer.subscription;
  if (sub.phase !== 'ended' || !sub.expiresOn || (sub.reportedMrr ?? 0) <= 0) return null;
  const month = shortDate(sub.expiresOn).split(' ').slice(1).join(' ');
  const text = `In MRR for ${month} (contract ended ${shortDate(sub.expiresOn)})`;
  return { month, endedOn: sub.expiresOn, text };
}

/**
 * The billing term worth saying: Yearly is 25 of the 42 contracts, so it is the
 * default and stays silent; a 6-month or monthly plan is the exception that earns the word.
 */
export function billingNote(customer: CustomerRow): string | null {
  return billingNoteFull(customer);
}

/** The list's version: "Monthly, invoiced yearly"; the invoice number behind it stays in the hover. */
export function billingNoteShort(customer: CustomerRow): string | null {
  const full = billingNoteFull(customer);
  return full ? full.replace(/ \([^)]*\)$/, '') : null;
}

function billingNoteFull(customer: CustomerRow): string | null {
  const plan = customer.subscription.planName;
  if (!plan || planDisplayName(plan).toLowerCase() === 'yearly') return null;
  return billingLabel(customer);
}
